import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, ApiError } from '../../shared/api/client';
import type { ContextOption, Me } from '../../shared/api/types';
import { setAppBadge } from '../../shared/push/app-badge';
import { disablePush } from '../../shared/push/push';

type SessionState = { status: 'loading' } | { status: 'anonymous' } | { status: 'authenticated'; me: Me };

interface SessionApi {
  state: SessionState;
  refresh: () => Promise<void>;
  login: (email: string, password: string, remember?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  switchContext: (churchId: string | null) => Promise<void>;
}

const SessionContext = createContext<SessionApi | null>(null);

/** Sesión del usuario: quién es, dónde actúa y qué puede hacer ahí (solo para la interfaz). */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: 'loading' });

  const refresh = useCallback(async () => {
    try {
      let me = await api.get<Me>('/security/me');
      // Con una sola Iglesia posible no hace falta preguntar: se entra directamente en ella.
      const only = me.contexts.length === 1 ? me.contexts[0] : undefined;
      if (!me.activeChurchId && only && !me.isPlatformAdmin) {
        await api.put('/security/context', { churchId: only.churchId });
        me = await api.get<Me>('/security/me');
      }
      setState({ status: 'authenticated', me });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) setState({ status: 'anonymous' });
      else throw error;
    }
  }, []);

  useEffect(() => {
    refresh().catch(() => setState({ status: 'anonymous' }));
  }, [refresh]);

  const value = useMemo<SessionApi>(
    () => ({
      state,
      refresh,
      login: async (email, password, remember = true) => {
        await api.post('/security/sessions', { email, password, remember });
        await refresh();
      },
      logout: async () => {
        // Este dispositivo deja de recibir los avisos de quien cierra sesión.
        await disablePush().catch(() => undefined);
        await api.delete('/security/sessions/current');
        setAppBadge(0);
        setState({ status: 'anonymous' });
      },
      switchContext: async (churchId) => {
        await api.put('/security/context', { churchId });
        await refresh();
      },
    }),
    [state, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionApi {
  const session = useContext(SessionContext);
  if (!session) throw new Error('useSession debe usarse dentro de <SessionProvider>');
  return session;
}

/** Datos de la sesión autenticada. Solo para componentes dentro de rutas protegidas. */
export function useMe(): Me & { activeContext: ContextOption | null; can: (permission: string) => boolean; canOwn: (permission: string) => boolean } {
  const { state } = useSession();
  if (state.status !== 'authenticated') throw new Error('useMe requiere sesión');
  const { me } = state;
  return {
    ...me,
    activeContext: me.contexts.find((c) => c.churchId === me.activeChurchId) ?? null,
    // Solo UX: el backend vuelve a autorizar cada acción.
    can: (permission) => me.permissions.some((p) => p.permission === permission && p.scope !== 'self'),
    // Permiso sobre uno mismo (alcance self) o más amplio.
    canOwn: (permission) => me.permissions.some((p) => p.permission === permission),
  };
}
